import { c as _c } from "react/compiler-runtime";
import { TextInput, Button, ActionIcon, Badge, Menu, Tooltip, Switch, Input, useCombobox, Combobox, ButtonProps } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { HiArrowRight, HiMinusCircle, HiPlusCircle, HiTrash, HiX } from 'react-icons/hi';
import { z } from 'zod';
import { usePortal } from '../portal';
import { match, P } from 'ts-pattern';
import { useAdminInvites } from './adminInvite';
import { InviteStatus, Invite as InviteType, emptyInvite, zInvite } from './inviteTypes';
import { OverflowMenu } from 'components/Menu';
import { MdSend } from 'react-icons/md';
import { zRoles } from 'shared/forecast/roleTypes';
import { entries, filterObj, notNullOrEmpty, isNullOrEmpty, groupMap } from 'shared/collection';
import { notNull, NotNull, safeParseList } from 'shared/type';
import { fmtDate, fmtDuration } from 'shared/format';
import { doSafe, okOrThrow } from 'shared/promise';
import { uri } from 'shared/uri';
import { HrText, LabelEmptyList, LabelForm, useElementSize, WrapIf } from 'shared/ui';
import { useEffect, useState } from 'react';
import { Workspace as W, type Workspace } from '../workspace';
import { openConfirmModal } from '@mantine/modals';
import { fromEntries, isEmpty, unique } from 'remeda';
import { DbFormSubset, type GetInputProps } from 'components/hooks/useDbForm';
import { DateTime as D } from 'luxon';
import { CopyLink } from 'components/form/CopyLink';
import { AdminSection, EditLimit, editLimitProps, RoleInput } from './AdminComponents';
import { isLinkActive, UseInviteLinks, useInviteLinks, useQrImageUrl } from './inviteLinks';
import { UseAdminUsersList, ProfileCell, useAdminUsersSection } from './AdminUsers';
import { InfoLabel, InfoTip } from 'components/Info';
import { env } from 'utils/env';
import { DateInput } from '@mantine/dates';
import { twMerge } from 'tailwind-merge';
import { SAvatar } from '../SAvatar';
import { useCtrlEnter } from 'components/Buttons';
import { partition } from 'remeda';
import { profileParticipantExpiry } from '../profileTypes';
import { useDebouncedValue } from 'components/hooks/useOnDebounce';
import { useQuery } from '@tanstack/react-query';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import { GraceAction, useGraceAction } from 'components/form/GraceButton';
const zInviteNewValidated = zInvite.pick({
  email: true,
  roles: true
}).extend({
  focus: z.boolean().optional()
});
type InviteNewValidated = z.infer<typeof zInviteNewValidated>;
export const AdminInviteLinks = t0 => {
  const $ = _c(73);
  const {
    uWorkspace,
    uUsers
  } = t0;
  const [showExpired, setShowExpired] = useState(false);
  let t1;
  if ($[0] !== showExpired || $[1] !== uWorkspace) {
    t1 = {
      uWorkspace,
      showExpired
    };
    $[0] = showExpired;
    $[1] = uWorkspace;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const inviteManager = useInviteLinks(t1);
  const adminSectionState = useAdminUsersSection("inviteLinks");
  if (!inviteManager.links || !inviteManager.workspace_id || !uWorkspace.form.values) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <></>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const participantsLimit = uUsers.usage.participantsLimit;
  let t2;
  if ($[4] !== uUsers.participants) {
    t2 = uUsers.participants ?? [];
    $[4] = uUsers.participants;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== participantsLimit.lastBillingDate) {
    t3 = p => participantsLimit.lastBillingDate == null || D.fromISO(p.created_at).toUnixInteger() >= participantsLimit.lastBillingDate.toUnixInteger();
    $[6] = participantsLimit.lastBillingDate;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = partition(t2, t3);
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [currentParticipants, olderParticipants] = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      container: "max-w-[50em] flex flex-col"
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="w-[12em]">Participant invite links</div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== uUsers.license) {
    t7 = u => <><b className="pl-4 ">{u.value}</b><span className="opacity-60 text-sm"><span> participants </span>since <span className="font-bold">{fmtDate(u.lastBillingDate, {
            disableRelative: true
          })}</span></span>{uUsers.license && <><span className="opacity-30 px-2 text-sm"> / </span><b className="opacity-60">{uUsers.license?.participants}</b><span className="opacity-60 text-sm"> in plan</span>{u.additional != null && u.additional > 0 && <><b className="opacity-80 ml-4">+{u.additional}</b><span className="opacity-60 text-sm"> additional</span></>}</>}</>;
    $[13] = uUsers.license;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== participantsLimit || $[16] !== t7) {
    t8 = doSafe(participantsLimit, t7);
    $[15] = participantsLimit;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="flex items-center gap-2">{t6}<div className="text-[--mantine-color-text] ">{t8}</div></div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = inviteManager.isDirty;
  const t11 = inviteManager.links.length;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <InfoLabel>Participants can click on links to join this workspace and start forecasting. They can forecast and comment for{fmtDuration(profileParticipantExpiry, {
        prefix: " "
      })} after joining. Expired participants can still join again using these links, but they receive a new profile and are treated as a new participant.</InfoLabel>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = uWorkspace.form as any;
  let t14;
  if ($[21] !== participantsLimit || $[22] !== t13) {
    t14 = editLimitProps({
      workspaceForm: t13,
      limit: participantsLimit
    });
    $[21] = participantsLimit;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = <EditLimit {...t14} />;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== uUsers) {
    t16 = id => uUsers.update({
      id,
      deleted: true
    });
    $[26] = uUsers;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== participantsLimit.lastBillingDate) {
    t17 = fmtDate(participantsLimit.lastBillingDate, {
      disableRelative: true
    });
    $[28] = participantsLimit.lastBillingDate;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const t18 = `Participants since ${t17}`;
  let t19;
  if ($[30] !== currentParticipants || $[31] !== t16 || $[32] !== t18) {
    t19 = <ParticipantAvatars participants={currentParticipants} onRemove={t16} title={t18} />;
    $[30] = currentParticipants;
    $[31] = t16;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== uUsers) {
    t20 = id_0 => uUsers.update({
      id: id_0,
      deleted: true
    });
    $[34] = uUsers;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== olderParticipants || $[37] !== t20) {
    t21 = <ParticipantAvatars participants={olderParticipants} onRemove={t20} title="Older participants" />;
    $[36] = olderParticipants;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t19 || $[40] !== t21) {
    t22 = <div className="flex flex-col gap-4 my-4">{t19}{t21}</div>;
    $[39] = t19;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <InfoLabel className="max-w-[60em]">These are links that can be used by anyone to gain access this workspace (they can even be forwarded on). They are good for when you don't have everyone's email (e.g. in a workshop). You can disable them, and if the data is sensitive you should be diligent about this.</InfoLabel>;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== inviteManager.createInviteLink) {
    t24 = <div className="rounded-lg border p-4 space-y-4">{t23}<InviteLinkForm onSubmit={inviteManager.createInviteLink} compact={false} submitLabel="Create Link" /></div>;
    $[43] = inviteManager.createInviteLink;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== inviteManager.links.length) {
    t25 = inviteManager.links.length == 0 && <LabelEmptyList className="my-4">No open invite links yet...</LabelEmptyList>;
    $[45] = inviteManager.links.length;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e => setShowExpired(e.currentTarget.checked);
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== showExpired) {
    t27 = <Switch label="Show expired links" checked={showExpired} onChange={t26} />;
    $[48] = showExpired;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== inviteManager || $[51] !== showExpired) {
    let t29;
    if ($[53] !== showExpired) {
      t29 = l => isLinkActive({
        l,
        showExpired
      });
      $[53] = showExpired;
      $[54] = t29;
    } else {
      t29 = $[54];
    }
    let t30;
    if ($[55] !== inviteManager) {
      t30 = link => <InviteLinkAdmin key={link.id} {...{
        link,
        form: inviteManager.getInviteLinkForm(link.id)
      }} />;
      $[55] = inviteManager;
      $[56] = t30;
    } else {
      t30 = $[56];
    }
    t28 = inviteManager.links.filter(t29).map(t30);
    $[50] = inviteManager;
    $[51] = showExpired;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[57] !== t27 || $[58] !== t28) {
    t29 = <div className="flex flex-col gap-4 mt-4 ">{t27}{t28}</div>;
    $[57] = t27;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t25 || $[61] !== t29) {
    t30 = <div>{t25}{t29}</div>;
    $[60] = t25;
    $[61] = t29;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== adminSectionState || $[64] !== inviteManager.isDirty || $[65] !== inviteManager.links.length || $[66] !== t12 || $[67] !== t15 || $[68] !== t22 || $[69] !== t24 || $[70] !== t30 || $[71] !== t9) {
    t31 = <AdminSection classNames={t5} {...adminSectionState} title={t9} isDirty={t10} num={t11} startCollapsed={true}>{t12}{t15}{t22}{t24}{t30}</AdminSection>;
    $[63] = adminSectionState;
    $[64] = inviteManager.isDirty;
    $[65] = inviteManager.links.length;
    $[66] = t12;
    $[67] = t15;
    $[68] = t22;
    $[69] = t24;
    $[70] = t30;
    $[71] = t9;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
};
const ParticipantAvatars = t0 => {
  const $ = _c(4);
  const {
    participants,
    onRemove,
    title
  } = t0;
  let t1;
  if ($[0] !== onRemove || $[1] !== participants || $[2] !== title) {
    t1 = isNullOrEmpty(participants) ? null : <div className="flex gap-1 flex-wrap items-center">{title && <div className="font-semibold opacity-40 mr-2">{title}</div>}{participants.map(p => <div key={p.id}><SAvatar size="sm" style={{
          filter: p.deleted ? "grayscale(100%)" : undefined,
          opacity: p.deleted ? 0.5 : undefined
        }} profile={p.cfg} tip={<div className="flex flex-col gap-2 p-2"><h3>{p.cfg.name}</h3>{p.seen && <div><span className="opacity-60">Last seen </span><span className="font-semibold">{fmtDate(D.fromISO(p.seen))}</span></div>}{p.deleted ? <Badge color="gray">Expired/removed</Badge> : <><div><span className="opacity-60">Expires </span><span className="font-semibold">{fmtDuration(D.fromISO(p.created_at).plus(profileParticipantExpiry).diffNow())}</span></div><Button variant="filled" color="red" onClick={() => {
              onRemove(p.id);
            }}>Remove</Button></>}</div>} /></div>)}</div>;
    $[0] = onRemove;
    $[1] = participants;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const InviteLinkCard = t0 => {
  const $ = _c(28);
  const {
    form,
    link,
    className,
    copyButtonProps,
    hidePath: t1
  } = t0;
  const hidePath = t1 === undefined ? false : t1;
  const qrImageUrl = useQrImageUrl(link.url);
  let t2;
  if ($[0] !== form) {
    t2 = {
      action: async () => {
        if (!form) {
          return;
        }
        form.setFieldValue("disabled", true);
      }
    };
    $[0] = form;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const delGrace = useGraceAction(t2);
  let t3;
  if ($[2] !== className) {
    t3 = twMerge("flex flex-col self-center border py-4 px-6 rounded-md", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== link.id || $[5] !== link.join) {
    t4 = link.join && <><div className="flex gap-4 items-center text-lg font-bold"><div><LabelForm className="text-sm">Go here</LabelForm><div className="">{uri(env.NEXT_PUBLIC_JOIN_URL).domainAndPath}</div></div><HiArrowRight className="opacity-30" /><div><div><LabelForm className="text-sm">Enter this code</LabelForm></div><div className="font-code tracking-widest uppercase">{link.id}</div></div></div><HrText>or</HrText></>;
    $[4] = link.id;
    $[5] = link.join;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== qrImageUrl) {
    t5 = <img src={qrImageUrl} className="w-[12em] self-center" />;
    $[7] = qrImageUrl;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <HrText>or</HrText>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== copyButtonProps || $[11] !== link.url) {
    t7 = <CopyLink className="self-center" url={link.url} hideUrl={true} buttonProps={copyButtonProps}>Copy link</CopyLink>;
    $[10] = copyButtonProps;
    $[11] = link.url;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t7) {
    t8 = <div className={t3}>{t4}{t5}{t6}{t7}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== delGrace || $[19] !== form || $[20] !== hidePath || $[21] !== link.created || $[22] !== link.expires || $[23] !== link.path) {
    t9 = form && <><div className="flex items-center gap-3"><div><span className="opacity-60 ">Created </span><b>{fmtDate(D.fromISO(link.created))}</b></div>{doSafe(link.expires, e => {
          const expires = D.fromISO(e);
          const ago = expires.diffNow();
          const expired = ago.milliseconds < 0;
          const status = expired ? "expired" : ago.as("days") > 2 ? "valid" : "expires-soon" as const;
          const color = `var(--mantine-color-${match(status).with("expired", _temp).with("expires-soon", _temp2).with("valid", _temp3).exhaustive()}-6)`;
          return <div className="flex items-center gap-0"><span className="opacity-60 ">{expired ? "Expired" : "Expires"} </span><Tooltip label="Bring forward expiry by 1 day"><ActionIcon className="opacity-30 hover:opacity-100" onClick={() => form?.setFieldValue("expires", expires.minus({
                days: 1
              }).toISO())}><HiMinusCircle /></ActionIcon></Tooltip><b style={{
              color
            }}>{fmtDuration(ago)}</b><Tooltip label="Extend by 1 day"><ActionIcon className="opacity-30 hover:opacity-100" onClick={() => form?.setFieldValue("expires", expires.plus({
                days: 1
              }).toISO())}><HiPlusCircle /></ActionIcon></Tooltip><Tooltip label="Delete this invite link"><GraceAction {...delGrace} cancelLabel="undo" color="red" className=" ml-2"><HiTrash /></GraceAction></Tooltip></div>;
        })}</div>{link.path && !hidePath && <div className="self-center"><span className="opacity-60">Links to </span><Badge variant="light" component="a" size="md" className="!cursor-pointer !normal-case" href={uri(env.URL).addPath(link.path).url}>{link.path}</Badge><span className="opacity-60"> with access to the workspace</span></div>}</>;
    $[18] = delGrace;
    $[19] = form;
    $[20] = hidePath;
    $[21] = link.created;
    $[22] = link.expires;
    $[23] = link.path;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <div className="flex flex-col gap-1 items-center self-center ">{t8}{t9}</div>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
type InviteLinkItem = NotNull<UseInviteLinks['links']>[number];
const InviteLinkAdmin = t0 => {
  const $ = _c(9);
  const {
    form,
    link: d
  } = t0;
  let t1;
  if ($[0] !== form) {
    t1 = <div className="flex p-2 gap-4"><InviteFormInputs form={form} /></div>;
    $[0] = form;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== d || $[3] !== form) {
    t2 = <div className="space-y-2">{d.disabled ? <span className="text-gray-400">Disabled</span> : <><div className="flex flex-col gap-6 items-start"><div className="flex items-center gap-4"><div className="flex items-center"><Switch className="m-2" {...form.getInputProps("noPrompt", {
                type: "checkbox"
              })} /><InfoTip label="Will not be prompted with tips or other popups when landing">No prompts</InfoTip></div></div><InviteLinkCard link={d} form={form} /></div></>}</div>;
    $[2] = d;
    $[3] = form;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== d.id || $[6] !== t1 || $[7] !== t2) {
    t3 = <div key={d.id} className="space-y-2 border rounded-md p-4">{t1}{t2}</div>;
    $[5] = d.id;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export const InviteFormInputs = t0 => {
  const $ = _c(6);
  const {
    form
  } = t0;
  let t1;
  if ($[0] !== form) {
    t1 = form.getInputProps("expires");
    $[0] = form;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const expiresProps = t1;
  const values = form.values;
  let t2;
  if ($[2] !== expiresProps || $[3] !== form || $[4] !== values) {
    const expires = doSafe(values?.expires, _temp4);
    t2 = values && <><TextInput label={<InfoTip label="A name for you to remember what the link was for">Invite Name</InfoTip>} className="grow max-w-[30em]" size="md" placeholder="For workshop attendees" {...form.getInputProps("name")} /><Input.Wrapper label={<InfoTip className="text-nowrap" label="If checked, users will be prompted to sign-in with email. They will count towards total users (i.e. not participants)">Require sign-in</InfoTip>}><Switch size="md" className="m-2" checked={!values.anon} onChange={e_0 => form.setFieldValue("anon", !e_0.currentTarget.checked)} /></Input.Wrapper>{!values.anon && <RoleInput label="Default roles" {...form.getInputProps("roles")} />}<DateInput w="10em" size="md" label="Expires" valueFormat="D MMM YYYY" getDayProps={_temp5} styles={{
        input: {
          color: expires && expires.diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : undefined
        }
      }} error={expiresProps.error} value={expires?.toISODate()} onChange={v => expiresProps.onChange(v ?? undefined)} clearable={true} /></>;
    $[2] = expiresProps;
    $[3] = form;
    $[4] = values;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
var domainNameRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]+$/i;
export const AdminInviteDomains = t0 => {
  const $ = _c(56);
  const {
    uWorkspace
  } = t0;
  const {
    form: f
  } = uWorkspace;
  const adminSectionState = useAdminUsersSection("inviteDomains");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = W.zDomainCfgNew.parse({
      roles: ["forecaster"]
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== f.values) {
    t2 = {
      initialValues: t1,
      validate: {
        domain: d => (domainNameRegex.test(d) ? undefined : "Invalid domain") ?? (d in f.values.domains ? "Domain already exists" : undefined),
        roles: _temp8
      }
    };
    $[1] = f.values;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const fNew = useForm(t2);
  if (!f.values) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <></>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== adminSectionState || $[5] !== f || $[6] !== fNew) {
    const domains = entries(f.values.domains).map(_temp9);
    T0 = AdminSection;
    t6 = adminSectionState;
    t7 = f.isDirty;
    t8 = domains.map(_temp10);
    let t10;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <InfoLabel className="max-w-[60em]">Usefull when you want anyone within a particular organization to have access. It means that new users with a company can sign up and gain access to this workspace without being explicitly invited.</InfoLabel>;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== fNew) {
      t11 = fNew.getInputProps("domain");
      $[16] = fNew;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    let t12;
    if ($[18] !== t11) {
      t12 = <TextInput label="Domain" className="w-2/3" size="md" placeholder="example.com" {...t11} />;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] !== fNew) {
      t13 = fNew.getInputProps("roles");
      $[20] = fNew;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    let t14;
    if ($[22] !== t13) {
      t14 = <RoleInput label="Default roles for this domain" {...t13} />;
      $[22] = t13;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    let t15;
    if ($[24] !== fNew) {
      t15 = <div className="flex gap-4 items-center mt-7"><ActionIcon className=""><HiX size={18} onClick={() => {
            fNew.reset();
          }} /></ActionIcon></div>;
      $[24] = fNew;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    let t16;
    if ($[26] !== t12 || $[27] !== t14 || $[28] !== t15) {
      t16 = <div className="flex gap-4 items-start">{t12}{t14}{t15}</div>;
      $[26] = t12;
      $[27] = t14;
      $[28] = t15;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== f || $[31] !== fNew) {
      t17 = () => {
        const res = fNew.validate();
        if (res.hasErrors) {
          return;
        }
        if (fNew.values.domain in f.values.domains) {
          fNew.setErrors({
            domain: "Domain already exists"
          });
          return;
        }
        f.setFieldValue(`domains.${fNew.values.domain.replaceAll(".", "_").toLowerCase()}`, fNew.values);
        fNew.reset();
      };
      $[30] = f;
      $[31] = fNew;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const t18 = !fNew.isValid;
    let t19;
    if ($[33] !== t17 || $[34] !== t18) {
      t19 = <div className="flex justify-end"><Button className="" onClick={t17} disabled={t18}>Add Domain</Button></div>;
      $[33] = t17;
      $[34] = t18;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    if ($[36] !== t16 || $[37] !== t19) {
      t9 = <div className="rounded-lg border p-4 space-y-4">{t10}{t16}{t19}</div>;
      $[36] = t16;
      $[37] = t19;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    t5 = domains.length == 0 && <LabelEmptyList className="my-4">No open sign-up domains yet...</LabelEmptyList>;
    t4 = "border-separate border-spacing-4 ";
    let t20;
    if ($[39] !== f) {
      t20 = (d_2, i) => <tr key={i}><td><h3>{d_2.domain}</h3></td><td><RoleInput label="Default roles for this domain" {...f.getInputProps(`domains.${d_2.key}.roles`)} /></td><td><OverflowMenu><Menu.Item leftSection={<HiTrash />} onClick={() => {
              openConfirmModal({
                title: "Remove open sign-up?",
                centered: true,
                children: <p>Are you sure you want to remove open sign-up for <b>{d_2.domain}</b></p>,
                labels: {
                  confirm: "Remove",
                  cancel: "Cancel"
                },
                confirmProps: {
                  color: "red",
                  variant: "filled"
                },
                onConfirm: async () => {
                  f.setFieldValue("domains", filterObj(f.values.domains, k => k != d_2.key) as any);
                }
              });
            }}>Remove domain</Menu.Item></OverflowMenu></td></tr>;
      $[39] = f;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    t3 = domains.map(t20);
    $[4] = adminSectionState;
    $[5] = f;
    $[6] = fNew;
    $[7] = T0;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    T0 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[41] !== t3) {
    t10 = <tbody>{t3}</tbody>;
    $[41] = t3;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t4) {
    t11 = <table className={t4}>{t10}</table>;
    $[43] = t10;
    $[44] = t4;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11 || $[47] !== t5) {
    t12 = <div>{t5}{t11}</div>;
    $[46] = t11;
    $[47] = t5;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== T0 || $[50] !== t12 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t13 = <T0 {...t6} isDirty={t7} num={t8}>{t9}{t12}</T0>;
    $[49] = T0;
    $[50] = t12;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
};
export const AdminEmailInvites = t0 => {
  const $ = _c(68);
  const {
    uUsers
  } = t0;
  const {
    profile
  } = usePortal();
  let t1;
  if ($[0] !== profile) {
    t1 = profile ?? {};
    $[0] = profile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    workspace_id
  } = t1;
  let t2;
  if ($[2] !== uUsers) {
    t2 = {
      uUsers
    };
    $[2] = uUsers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    invites: storedInvites,
    sendInvites,
    cancelInvite
  } = useAdminInvites(t2);
  const {
    ref,
    width
  } = useElementSize();
  const {
    emailCompletions
  } = uUsers;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      invites: [{
        ...emptyInvite(),
        focus: false
      }]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== uUsers.users) {
    t4 = {
      initialValues: t3,
      validate: v => {
        const errors = zodResolver(z.object({
          invites: z.array(zInviteNewValidated).min(1, {
            message: "need at least one invite"
          })
        }))(v);
        if (!isEmpty(errors)) {
          return errors;
        }
        return fromEntries(safeParseList(zInviteNewValidated, v.invites).map((u, i) => {
          const exists = uUsers.users?.find(b => b.email == u.email);
          return exists ? [`invites.${i}.email`, "already a user"] as const : undefined;
        }).filter(notNullOrEmpty));
      }
    };
    $[5] = uUsers.users;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    getInputProps: getInputPropsInvite,
    setValues: setInvites,
    values: t5,
    validate
  } = useForm(t4);
  const {
    invites: newInvites
  } = t5;
  let t6;
  if ($[7] !== newInvites || $[8] !== setInvites) {
    t6 = () => setInvites({
      invites: [...(newInvites.map(_temp11) ?? []), {
        ...emptyInvite(),
        focus: true
      }]
    });
    $[7] = newInvites;
    $[8] = setInvites;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onInviteMore = t6;
  let t7;
  if ($[10] !== newInvites || $[11] !== profile || $[12] !== sendInvites || $[13] !== setInvites || $[14] !== validate) {
    t7 = async () => {
      if (validate().hasErrors || !profile) {
        return;
      }
      await sendInvites.mutateAsync({
        profile,
        newInvites: safeParseList(zInviteNewValidated, newInvites)
      }, {
        onSuccess: () => setInvites({
          invites: [{
            ...emptyInvite(),
            focus: false
          }]
        })
      });
    };
    $[10] = newInvites;
    $[11] = profile;
    $[12] = sendInvites;
    $[13] = setInvites;
    $[14] = validate;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const onSendInvites = t7;
  const statusColor = _temp17;
  let t8;
  if ($[16] !== cancelInvite || $[17] !== profile || $[18] !== sendInvites || $[19] !== workspace_id) {
    t8 = i_1 => ({
      Profile: () => workspace_id ? <ProfileCell {...i_1} /> : undefined,
      Roles: () => <>{i_1.roles.map(_temp18)}</>,
      Status: () => <div className="space-y-2"><div className="flex items-center gap-2"><Badge color={statusColor(i_1.status)}>{i_1.status}</Badge>{fmtDate(i_1.created_at.toISOString(), {
            showTz: true
          })}</div>{match(i_1.status).with("authenticated", _temp19).otherwise(_temp20)}{i_1.status_msg && <div>{i_1.status_msg}</div>}</div>,
      Menu: () => <OverflowMenu width={150}><Menu.Item leftSection={<MdSend />} onClick={() => {
          if (!profile) {
            return;
          }
          sendInvites.mutate({
            profile,
            newInvites: [i_1]
          });
        }}>Resend</Menu.Item><Menu.Item leftSection={<HiX />} onClick={() => {
          cancelInvite(i_1.id);
        }}>Cancel</Menu.Item></OverflowMenu>
    });
    $[16] = cancelInvite;
    $[17] = profile;
    $[18] = sendInvites;
    $[19] = workspace_id;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const cells = t8;
  const adminSectionState = useAdminUsersSection("emailInvites");
  let t9;
  if ($[21] !== onSendInvites) {
    t9 = {
      onClick: onSendInvites
    };
    $[21] = onSendInvites;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const ctrlEnter = useCtrlEnter(t9);
  let t10;
  if ($[23] !== storedInvites) {
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = (s_1, g) => g.length == 0 ? null : <Badge key={s_1} color={statusColor(s_1)}>{s_1} {g.length}</Badge>;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = groupMap(storedInvites ?? [], _temp21, t11).filter(notNull);
    $[23] = storedInvites;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = ctrlEnter.onKeyDown;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <InfoLabel>Invite users with via email. This is more secure than invite links and doesn't require a separate email verification step for the invited user</InfoLabel>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== emailCompletions || $[28] !== getInputPropsInvite || $[29] !== newInvites || $[30] !== onInviteMore || $[31] !== sendInvites.isPending || $[32] !== setInvites) {
    t13 = newInvites.map((invite, i_3) => <NewInviteRow key={i_3} invite={invite} inputProps={prop => getInputPropsInvite(`invites.${i_3}.${prop}`)} onInviteMore={e => {
      if (e) {
        setInvites({
          invites: [...newInvites.filter(_temp22), ...e.map(email => ({
            ...invite,
            email
          })), {
            ...emptyInvite(),
            roles: invite.roles,
            focus: true
          }]
        });
      } else {
        onInviteMore();
      }
    }} onRemoveInvite={() => setInvites({
      invites: newInvites.filter((_, j) => i_3 != j)
    })} emailCompletions={emailCompletions} disabled={sendInvites.isPending} />);
    $[27] = emailCompletions;
    $[28] = getInputPropsInvite;
    $[29] = newInvites;
    $[30] = onInviteMore;
    $[31] = sendInvites.isPending;
    $[32] = setInvites;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <HiPlusCircle />;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== onInviteMore || $[36] !== sendInvites.isPending) {
    t15 = <div><Button leftSection={t14} disabled={sendInvites.isPending} className="float-right" onClick={onInviteMore}>invite more</Button></div>;
    $[35] = onInviteMore;
    $[36] = sendInvites.isPending;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <hr className="mt-4" />;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== ctrlEnter.buttonProps || $[40] !== sendInvites.isPending || $[41] !== uUsers.usage.usersLimit.exceeding) {
    t17 = <Button className="self-end" loading={sendInvites.isPending} {...ctrlEnter.buttonProps} disabled={uUsers.usage.usersLimit.exceeding}>Invite User</Button>;
    $[39] = ctrlEnter.buttonProps;
    $[40] = sendInvites.isPending;
    $[41] = uUsers.usage.usersLimit.exceeding;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17 || $[44] !== uUsers.usage.usersLimit.exceeding) {
    t18 = <WrapIf if={uUsers.usage.usersLimit.exceeding} wrap={_temp23}>{t17}</WrapIf>;
    $[43] = t17;
    $[44] = uUsers.usage.usersLimit.exceeding;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t13 || $[47] !== t15 || $[48] !== t18) {
    t19 = <div className="flex flex-col gap-4 pt-4">{t13}{t15}{t16}{t18}</div>;
    $[46] = t13;
    $[47] = t15;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== ctrlEnter.onKeyDown || $[51] !== t19) {
    t20 = <div className="rounded-lg border p-6 mt-4" onKeyDown={t11}>{t12}{t19}</div>;
    $[50] = ctrlEnter.onKeyDown;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== storedInvites?.length) {
    t21 = storedInvites?.length == 0 && <p className="pt-4 text-center text-gray-500">No outstanding invites</p>;
    $[53] = storedInvites?.length;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== cells || $[56] !== storedInvites || $[57] !== width) {
    t22 = width > 500 ? <table className="w-full border-separate border-spacing-y-4 "><tbody>{storedInvites?.map(i_5 => {
          const {
            Profile,
            Roles,
            Status,
            Menu: Menu$0
          } = cells(i_5);
          return <tr key={i_5.id}><td className=""><Profile /></td><td><Roles /></td><td><Status /></td><td><Menu$0 /></td></tr>;
        })}</tbody></table> : <div className="space-y-8">{storedInvites?.map(i_6 => {
        const {
          Profile: Profile_0,
          Roles: Roles_0,
          Status: Status_0,
          Menu: Menu_0
        } = cells(i_6);
        return <div key={i_6.id} className="flex justify-between"><div className="space-y-2"><Profile_0 /><div className="flex grow flex-wrap items-center gap-4"><Status_0 /><Roles_0 /></div></div><Menu_0 /></div>;
      })}</div>;
    $[55] = cells;
    $[56] = storedInvites;
    $[57] = width;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== ref || $[60] !== t21 || $[61] !== t22) {
    t23 = <div ref={ref}>{t21}{t22}</div>;
    $[59] = ref;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== adminSectionState || $[64] !== t10 || $[65] !== t20 || $[66] !== t23) {
    t24 = <AdminSection {...adminSectionState} startCollapsed={true} num={t10}>{t20}{t23}</AdminSection>;
    $[63] = adminSectionState;
    $[64] = t10;
    $[65] = t20;
    $[66] = t23;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  return t24;
};
const NewInviteRow = t0 => {
  const $ = _c(61);
  const {
    invite,
    inputProps,
    onInviteMore,
    onRemoveInvite,
    disabled,
    emailCompletions
  } = t0;
  const store = useCombobox();
  let t1;
  if ($[0] !== inputProps) {
    t1 = inputProps("email");
    $[0] = inputProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let emailInProps;
  let onChange;
  let value;
  if ($[2] !== t1) {
    ({
      onChange,
      value,
      ...emailInProps
    } = t1);
    $[2] = t1;
    $[3] = emailInProps;
    $[4] = onChange;
    $[5] = value;
  } else {
    emailInProps = $[3];
    onChange = $[4];
    value = $[5];
  }
  const [emailDebounced] = useDebouncedValue(value, 200);
  const [hasSelected, setHasSelected] = useState(false);
  let t2;
  if ($[6] !== emailDebounced) {
    t2 = ["emailCompletions", emailDebounced];
    $[6] = emailDebounced;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== emailCompletions || $[9] !== value) {
    t3 = async () => value == null || value.length <= 2 ? [] : okOrThrow(await emailCompletions(value)).data;
    $[8] = emailCompletions;
    $[9] = value;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const emailCompletionsQuery = useQuery(t4);
  let t5;
  if ($[14] !== emailCompletionsQuery.data || $[15] !== hasSelected || $[16] !== store) {
    t5 = () => {
      if (!hasSelected && !store.dropdownOpened && emailCompletionsQuery.data && emailCompletionsQuery.data.length > 0) {
        store.openDropdown();
      }
      if (store.dropdownOpened && emailCompletionsQuery.data && emailCompletionsQuery.data.length == 0) {
        store.closeDropdown();
      }
    };
    $[14] = emailCompletionsQuery.data;
    $[15] = hasSelected;
    $[16] = store;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== emailCompletionsQuery.data || $[19] !== hasSelected) {
    t6 = [emailCompletionsQuery.data, hasSelected];
    $[18] = emailCompletionsQuery.data;
    $[19] = hasSelected;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  if ($[21] !== onChange || $[22] !== store) {
    t7 = email => {
      store.closeDropdown();
      setHasSelected(true);
      onChange(email);
    };
    $[21] = onChange;
    $[22] = store;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <InfoTip label="You can also paste a list of emails from a spreadsheet to do this in bulk">Email</InfoTip>;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== onChange) {
    t9 = e => {
      setHasSelected(false);
      onChange(e);
    };
    $[25] = onChange;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== onInviteMore || $[28] !== onRemoveInvite) {
    t10 = e_0 => {
      const txt = e_0.clipboardData.getData("text");
      const emails = txt.split(/\n/).map(_temp24).filter(_temp25);
      if (emails.length <= 1) {
        return;
      }
      e_0.preventDefault();
      onRemoveInvite();
      onInviteMore(emails);
    };
    $[27] = onInviteMore;
    $[28] = onRemoveInvite;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== disabled || $[31] !== emailInProps || $[32] !== invite.focus || $[33] !== t10 || $[34] !== t9 || $[35] !== value) {
    t11 = <Combobox.Target><TextInput label={t8} className="w-2/3" size="md" autoFocus={invite.focus} {...emailInProps} value={value} onChange={t9} disabled={disabled} onPaste={t10} /></Combobox.Target>;
    $[30] = disabled;
    $[31] = emailInProps;
    $[32] = invite.focus;
    $[33] = t10;
    $[34] = t9;
    $[35] = value;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Combobox.Header className="opacity-70">Matching emails in workspaces you administer</Combobox.Header>;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== emailCompletionsQuery) {
    t13 = match(emailCompletionsQuery).with({
      isLoading: true
    }, _temp26).with({
      data: P.not(P.nullish)
    }, _temp29).otherwise(_temp30);
    $[38] = emailCompletionsQuery;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13) {
    t14 = <Combobox.Dropdown className="max-h-[50vh] overflow-y-auto !max-w-[98vw]">{t12}<Combobox.Options className="space-y-2">{t13}</Combobox.Options></Combobox.Dropdown>;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== store || $[43] !== t11 || $[44] !== t14 || $[45] !== t7) {
    t15 = <Combobox store={store} onOptionSubmit={t7}>{t11}{t14}</Combobox>;
    $[42] = store;
    $[43] = t11;
    $[44] = t14;
    $[45] = t7;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== inputProps) {
    t16 = inputProps("roles");
    $[47] = inputProps;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== disabled || $[50] !== t16) {
    t17 = <RoleInput {...t16} disabled={disabled} />;
    $[49] = disabled;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== onRemoveInvite) {
    t18 = <HiX size={18} onClick={() => onRemoveInvite()} />;
    $[52] = onRemoveInvite;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== disabled || $[55] !== t18) {
    t19 = <ActionIcon className="ml-2 mt-7" disabled={disabled}>{t18}</ActionIcon>;
    $[54] = disabled;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t15 || $[58] !== t17 || $[59] !== t19) {
    t20 = <div className="flex gap-4 items-center">{t15}{t17}{t19}</div>;
    $[57] = t15;
    $[58] = t17;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  return t20;
};
const zInviteLinkForm = WT.zInviteLinkNew.extend({
  path: z.string().optional()
});
type InviteLinkFormData = z.infer<typeof zInviteLinkForm>;
interface InviteLinkFormProps {
  onSubmit: (data: InviteLinkFormData) => void;
  onCancel?: () => void;
  initialValues?: Partial<InviteLinkFormData>;
  disabled?: boolean;
  submitLabel?: string;
  compact?: boolean;
}
const InviteLinkForm = t0 => {
  const $ = _c(93);
  const {
    onSubmit,
    onCancel,
    initialValues: t1,
    disabled: t2,
    submitLabel: t3,
    compact: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const initialValues = t5;
  const disabled = t2 === undefined ? false : t2;
  const submitLabel = t3 === undefined ? "Create Link" : t3;
  const compact = t4 === undefined ? false : t4;
  let t6;
  if ($[2] !== compact) {
    t6 = compact ? `Shared on ${D.now().toFormat("MMM d, yyyy")}` : "";
    $[2] = compact;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["forecaster"];
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = D.now().plus({
      days: 7
    }).toISO();
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== initialValues || $[7] !== t6) {
    t9 = {
      name: t6,
      roles: t7,
      anon: true,
      noPrompt: false,
      expires: t8,
      ...initialValues
    };
    $[6] = initialValues;
    $[7] = t6;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      roles: _temp33
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t9) {
    t11 = {
      initialValues: t9,
      validate: t10
    };
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const form = useForm(t11);
  let t12;
  if ($[12] !== form) {
    t12 = form.getInputProps("expires");
    $[12] = form;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const expiresProps = t12;
  const values = form.values;
  let T0;
  let handleSubmit;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[14] !== compact || $[15] !== disabled || $[16] !== expiresProps.error || $[17] !== form || $[18] !== onSubmit || $[19] !== values.anon || $[20] !== values.expires) {
    const expires = values?.expires ? D.fromISO(values.expires) : undefined;
    let t25;
    if ($[35] !== form || $[36] !== onSubmit) {
      t25 = () => {
        const res = form.validate();
        if (res.hasErrors) {
          return;
        }
        onSubmit(form.values);
        form.reset();
      };
      $[35] = form;
      $[36] = onSubmit;
      $[37] = t25;
    } else {
      t25 = $[37];
    }
    handleSubmit = t25;
    t21 = `space-y-${compact ? "3" : "4"}`;
    if ($[38] !== compact || $[39] !== disabled || $[40] !== form) {
      t22 = !compact && <TextInput label={<InfoTip label="A name for you to remember what the link was for">Invite Name</InfoTip>} className="grow max-w-[30em]" size="md" placeholder="For workshop attendees" {...form.getInputProps("name")} disabled={disabled} />;
      $[38] = compact;
      $[39] = disabled;
      $[40] = form;
      $[41] = t22;
    } else {
      t22 = $[41];
    }
    let t26;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <InfoTip label="If checked, users will be prompted to sign-in with email. They will count towards total users (i.e. not participants)">Require sign-in</InfoTip>;
      $[42] = t26;
    } else {
      t26 = $[42];
    }
    const t27 = compact ? "sm" : "md";
    const t28 = !values.anon;
    let t29;
    if ($[43] !== form) {
      t29 = e => form.setFieldValue("anon", !e.currentTarget.checked);
      $[43] = form;
      $[44] = t29;
    } else {
      t29 = $[44];
    }
    if ($[45] !== disabled || $[46] !== t27 || $[47] !== t28 || $[48] !== t29) {
      t23 = <Input.Wrapper label={t26}><Switch size={t27} className="m-2" checked={t28} onChange={t29} disabled={disabled} /></Input.Wrapper>;
      $[45] = disabled;
      $[46] = t27;
      $[47] = t28;
      $[48] = t29;
      $[49] = t23;
    } else {
      t23 = $[49];
    }
    if ($[50] !== disabled || $[51] !== form || $[52] !== values.anon) {
      t24 = !values.anon && <RoleInput label="Default roles" {...form.getInputProps("roles")} disabled={disabled} />;
      $[50] = disabled;
      $[51] = form;
      $[52] = values.anon;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    T0 = DateInput;
    t13 = compact ? "8em" : "10em";
    t14 = compact ? "sm" : "md";
    t15 = "Expires";
    t16 = "D MMM YYYY";
    t17 = _temp34;
    const t30 = expires && expires.diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : undefined;
    if ($[54] !== t30) {
      t18 = {
        input: {
          color: t30
        }
      };
      $[54] = t30;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    t19 = expiresProps.error;
    t20 = expires?.toISODate();
    $[14] = compact;
    $[15] = disabled;
    $[16] = expiresProps.error;
    $[17] = form;
    $[18] = onSubmit;
    $[19] = values.anon;
    $[20] = values.expires;
    $[21] = T0;
    $[22] = handleSubmit;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
    $[34] = t24;
  } else {
    T0 = $[21];
    handleSubmit = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
    t19 = $[29];
    t20 = $[30];
    t21 = $[31];
    t22 = $[32];
    t23 = $[33];
    t24 = $[34];
  }
  let t25;
  if ($[56] !== expiresProps) {
    t25 = v => expiresProps.onChange(v ?? undefined);
    $[56] = expiresProps;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== T0 || $[59] !== disabled || $[60] !== t13 || $[61] !== t14 || $[62] !== t15 || $[63] !== t16 || $[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t20 || $[68] !== t25) {
    t26 = <T0 w={t13} size={t14} label={t15} valueFormat={t16} getDayProps={t17} styles={t18} error={t19} value={t20} onChange={t25} clearable={true} disabled={disabled} />;
    $[58] = T0;
    $[59] = disabled;
    $[60] = t13;
    $[61] = t14;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
    $[68] = t25;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  const t27 = `flex gap-2 ${compact ? "justify-end" : "justify-between"} items-center`;
  let t28;
  if ($[70] !== compact || $[71] !== disabled || $[72] !== onCancel) {
    t28 = onCancel && <Button variant="subtle" size={compact ? "sm" : "md"} onClick={onCancel} disabled={disabled}>Cancel</Button>;
    $[70] = compact;
    $[71] = disabled;
    $[72] = onCancel;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const t29 = compact ? "sm" : "md";
  let t30;
  if ($[74] !== disabled || $[75] !== form) {
    t30 = !form.isDirty() || disabled;
    $[74] = disabled;
    $[75] = form;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== handleSubmit || $[78] !== submitLabel || $[79] !== t29 || $[80] !== t30) {
    t31 = <Button variant="filled" size={t29} onClick={handleSubmit} disabled={t30}>{submitLabel}</Button>;
    $[77] = handleSubmit;
    $[78] = submitLabel;
    $[79] = t29;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== t27 || $[83] !== t28 || $[84] !== t31) {
    t32 = <div className={t27}>{t28}{t31}</div>;
    $[82] = t27;
    $[83] = t28;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== t21 || $[87] !== t22 || $[88] !== t23 || $[89] !== t24 || $[90] !== t26 || $[91] !== t32) {
    t33 = <div className={t21}>{t22}{t23}{t24}{t26}{t32}</div>;
    $[86] = t21;
    $[87] = t22;
    $[88] = t23;
    $[89] = t24;
    $[90] = t26;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  return t33;
};
function _temp() {
  return "red";
}
function _temp2() {
  return "orange";
}
function _temp3() {
  return "green";
}
function _temp4(e) {
  return D.fromISO(e);
}
function _temp5(d) {
  return {
    weekend: false,
    highlightToday: D.fromISO(d).hasSame(D.now(), "day"),
    style: {
      color: D.fromISO(d).diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : ""
    }
  };
}
function _temp6(t0) {
  const {
    data
  } = t0;
  return data.length == 0 ? "Must select at least one role" : undefined;
}
function _temp7(t0) {
  const {
    error
  } = t0;
  return error.message;
}
function _temp8(rs) {
  return match(z.array(zRoles).safeParse(rs)).with({
    success: true
  }, _temp6).with({
    success: false
  }, _temp7).exhaustive();
}
function _temp9(t0) {
  const [key, d_0] = t0;
  return {
    ...d_0,
    key
  };
}
function _temp10(d_1) {
  return <div key={d_1.key} className="flex gap-2"><Badge variant="light" styles={{
      label: {
        textTransform: "none",
        fontSize: "1.2em"
      }
    }}>{d_1.domain}</Badge></div>;
}
function _temp11(i_0) {
  return {
    ...i_0,
    focus: false
  };
}
function _temp12() {
  return "red";
}
function _temp13() {
  return "blue";
}
function _temp14() {
  return "orange";
}
function _temp15() {
  return "green";
}
function _temp16() {
  return "grey";
}
function _temp17(s) {
  return match(s).with("error", _temp12).with("sent", _temp13).with("expired", _temp14).with("authenticated", _temp15).otherwise(_temp16);
}
function _temp18(r) {
  return <Badge key={r}>{r}</Badge>;
}
function _temp19() {
  return <div className="text-sm opacity-70">User with that email has signed-in, but hasn't opened this workspace yet</div>;
}
function _temp20(s_0) {}
function _temp21(i_2) {
  return i_2.status;
}
function _temp22(i_4) {
  return !isNullOrEmpty(i_4.email);
}
function _temp23(c) {
  return <Tooltip label="Invites disabled because the user limit has been reached">{c}</Tooltip>;
}
function _temp24(t) {
  return t.trim();
}
function _temp25(e_1) {
  return e_1 != "";
}
function _temp26() {
  return <></>;
}
function _temp27(w) {
  return <Badge key={w} color="gray" variant="light">{w}</Badge>;
}
function _temp28(u) {
  return <Combobox.Option key={u.email} value={u.email} className={"space-y-2 data-[combobox-selected=\"true\"]:!bg-primary/10 !text-black/90"}><div className=""><span className="font-semibold">{u.email}</span>{u.name && <span className="opacity-60 ml-2"> ({u.name})</span>}</div><div className="flex gap-2 flex-wrap">{unique(u.workspaces).map(_temp27)}</div></Combobox.Option>;
}
function _temp29(t0) {
  const {
    data
  } = t0;
  return data.length == 0 ? <LabelEmptyList className="mt-6">None found</LabelEmptyList> : data.map(_temp28);
}
function _temp30() {
  return <></>;
}
function _temp31(t0) {
  const {
    data
  } = t0;
  return data.length == 0 ? "Must select at least one role" : undefined;
}
function _temp32(t0) {
  const {
    error
  } = t0;
  return error.message;
}
function _temp33(rs) {
  return match(z.array(zRoles).safeParse(rs)).with({
    success: true
  }, _temp31).with({
    success: false
  }, _temp32).exhaustive();
}
function _temp34(d) {
  return {
    weekend: false,
    highlightToday: D.fromISO(d).hasSame(D.now(), "day"),
    style: {
      color: D.fromISO(d).diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : ""
    }
  };
}